import { useState, useRef, useEffect, useCallback } from "react";

/**
 * useContextMenu
 * Hook, der den Öffnen/Schließen-Zustand und die Position eines
 * Rechtsklick-Menüs verwaltet.
 *
 * Verwendung:
 *   const { open, position, openMenu, closeMenu, menuRef } = useContextMenu();
 *
 *   <div onContextMenu={(e) => openMenu(e)}>...</div>
 *   {open && <ContextMenu ... />}
 */
function useContextMenu() {
   const [open, setOpen] = useState(false);
   const [position, setPosition] = useState({ x: 0, y: 0 });
   // context: beliebige Daten des angeklickten divs, z.B. { id, type }
   const [context, setContext] = useState(null);
   const menuRef = useRef(null);

   // data = beliebiges Objekt, das du dem jeweiligen div zuordnen willst,
   // z.B. { id: "42", type: "ordner" }
   const openMenu = useCallback((event, data) => {
      event.preventDefault(); // verhindert das native Browser-Kontextmenü
      setPosition({ x: event.clientX, y: event.clientY });
      setContext(data ?? null);
      setOpen(true);
   }, []);

   const closeMenu = useCallback(() => {
      setOpen(false);
      setContext(null);
   }, []);

   // Menü schließen bei Klick außerhalb, Escape oder Scroll
   useEffect(() => {
      if (!open) return;

      const handleClickOutside = (e) => {
         if (menuRef.current && !menuRef.current.contains(e.target)) {
            closeMenu();
         }
      };
      const handleKeyDown = (e) => {
         if (e.key === "Escape") closeMenu();
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("scroll", closeMenu, true);
      window.addEventListener("resize", closeMenu);

      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
         document.removeEventListener("keydown", handleKeyDown);
         document.removeEventListener("scroll", closeMenu, true);
         window.removeEventListener("resize", closeMenu);
      };
   }, [open, closeMenu]);

   return { open, position, context, openMenu, closeMenu, menuRef };
}

/**
 * ContextMenu
 * Das eigentliche Popup-Menü. Bekommt Position, Sichtbarkeit und
 * eine Liste von Items übergeben.
 *
 * items: [{ label: string, onClick: () => void, danger?: boolean }]
 */
function ContextMenu({ open, position, items, menuRef, closeMenu }) {
   if (!open) return null;

   // Menü nicht über den Bildschirmrand hinausragen lassen
   const style = {
      position: "fixed",
      top: position.y,
      left: position.x,
      zIndex: 1000,
   };

   return (
      <ul
         ref={menuRef}
         style={{
            ...style,
            minWidth: "180px",
            background: "#fff",
            border: "1px solid #e2e2e2",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            padding: "4px",
            margin: 0,
            listStyle: "none",
            fontFamily: "sans-serif",
            fontSize: "14px",
         }}
      >
         {items.map((item, i) => (
            <li key={i}>
               <button
                  onClick={() => {
                     item.onClick();
                     closeMenu();
                  }}
                  style={{
                     display: "block",
                     width: "100%",
                     textAlign: "left",
                     padding: "8px 12px",
                     background: "transparent",
                     border: "none",
                     borderRadius: "6px",
                     cursor: "pointer",
                     color: item.danger ? "#d33" : "#222",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f2f2f2")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
               >
                  {item.label}
               </button>
            </li>
         ))}
      </ul>
   );
}

/**
 * Baut die Menüpunkte abhängig vom Kontext des angeklickten divs.
 * context enthält alles, was du beim onContextMenu-Aufruf mitgegeben hast
 * (z.B. { id, type }).
 */
export function getItemsForContext(context, setLastAction) {
   if (!context) return [];

   if (context.type === "text") {
      return [
         {
            label: "Umbenennen",
            onClick: () => {
               window.alert("Text ausgewählt: " + context.content);
            },
         },
         {
            label: "Abbrechen",
            danger: true,
            onClick: () => {},
         },
      ];
   }

   if (context.type === "datei") {
      return [
         { label: "Herunterladen", onClick: () => setLastAction(`Datei ${context.id} herunterladen`) },
         { label: "Teilen", onClick: () => setLastAction(`Datei ${context.id} teilen`) },
         {
            label: "Löschen",
            danger: true,
            onClick: () => setLastAction(`Datei ${context.id} löschen`),
         },
      ];
   }

   return [];
}

/**
 * Beispiel: zwei unterschiedliche div-Typen (Ordner / Datei),
 * jeweils mit eigener id und eigenem Menü.
 */
export default function ContextMenuDemo() {
   const { open, position, context, openMenu, closeMenu, menuRef } = useContextMenu();
   const [lastAction, setLastAction] = useState("");

   const text = [
      { id: "ordner-1", name: "Dokumente" },
      { id: "ordner-2", name: "Bilder" },
   ];
   const dateien = [
      { id: "datei-1", name: "rechnung.pdf" },
      { id: "datei-2", name: "foto.png" },
   ];

   const items = getItemsForContext(context, setLastAction);

   return (
      <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>
         <h4>Ordner</h4>
         <div style={{ display: "flex", gap: "12px", marginBottom: "1.5rem" }}>
            {ordner.map((o) => (
               <div
                  key={o.id}
                  onContextMenu={(e) => openMenu(e, { id: o.id, type: "ordner" })}
                  style={{
                     width: "140px",
                     height: "80px",
                     background: "#eef4ff",
                     border: "1px solid #cddcf7",
                     borderRadius: "8px",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     userSelect: "none",
                  }}
               >
                  {o.name}
               </div>
            ))}
         </div>

         <h4>Dateien</h4>
         <div style={{ display: "flex", gap: "12px" }}>
            {dateien.map((d) => (
               <div
                  key={d.id}
                  onContextMenu={(e) => openMenu(e, { id: d.id, type: "datei" })}
                  style={{
                     width: "140px",
                     height: "80px",
                     background: "#f7f7f7",
                     border: "1px dashed #bbb",
                     borderRadius: "8px",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     userSelect: "none",
                  }}
               >
                  {d.name}
               </div>
            ))}
         </div>

         {lastAction && <p style={{ marginTop: "1.5rem" }}>Letzte Aktion: {lastAction}</p>}

         <ContextMenu open={open} position={position} items={items} menuRef={menuRef} closeMenu={closeMenu} />
      </div>
   );
}

export { useContextMenu, ContextMenu };
